'use client';

import { FC } from 'react';
import Image from 'next/image';
import { Content, asLink, isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { shortHostname } from '@/utils/urlUtils';
import { useIsNarrowMobile } from '@/slices/TeamMembersGrid/hooks/useIsNarrowMobile';

type Members =
  Content.TeamMembersGridSliceStackedRowsCards['primary']['members'];
type Member = NonNullable<Members>[number];
type SocialLinkField = Member['social_links'][number];

type SocialPair = {
  href: string;
  label: string | null;
  iconUrl: string | null;
};

// Editors add entries in order as `[link, icon, link, icon, …]`, tagging each
// with the "Social Media Link" or "Social Media Icon" variant. We walk the flat
// list: every "Social Media Link" opens a new entry; a "Social Media Icon" that
// follows one attaches its URL (a Prismic Media link resolves to the image URL)
// as that entry's icon. Bare icons with no preceding link are skipped.
const pairSocialLinks = (links: SocialLinkField[]): SocialPair[] => {
  const pairs: SocialPair[] = [];

  for (const entry of links) {
    const variant = (entry as { variant?: string }).variant;
    const url = asLink(entry);

    if (variant === 'Social Media Icon') {
      const last = pairs[pairs.length - 1];
      if (last && !last.iconUrl && url) {
        last.iconUrl = url;
      }
      continue;
    }

    if (isFilled.link(entry) && url) {
      pairs.push({ href: url, label: shortHostname(url), iconUrl: null });
    }
  }

  return pairs;
};

const SocialLinkItem: FC<{
  pair: SocialPair;
  memberName: string | null | undefined;
}> = ({ pair, memberName }) => {
  const ariaLabel = `${memberName ?? 'Team member'}${pair.label ? ` on ${pair.label}` : ''}`;

  if (pair.iconUrl) {
    return (
      <a
        href={pair.href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        className="inline-flex items-center justify-center opacity-80 transition-opacity duration-200 hover:opacity-100"
      >
        <Image
          src={pair.iconUrl}
          alt=""
          width={20}
          height={20}
          className="h-5 w-5 object-contain"
          unoptimized
        />
      </a>
    );
  }

  return (
    <a
      href={pair.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className="inline-flex items-center rounded-full bg-[#041118]/5 px-2 py-[2px] text-[12px] font-medium capitalize leading-none text-[#041118]/70 transition-colors duration-200 hover:bg-[#041118]/10 hover:text-[#041118]"
    >
      {pair.label ?? pair.href}
    </a>
  );
};

type Props = {
  members: Members;
};

export const MembersList: FC<Props> = ({ members }) => {
  const isMobileViewPort = useIsNarrowMobile();
  const list: Member[] = members ?? [];

  if (list.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-5 md:gap-6 lg:grid-cols-2">
      {list.map((member, index) => {
        const isPriority = index < 4;
        const socialPairs = pairSocialLinks(member.social_links ?? []);

        const photoBlock = isFilled.image(member.photo) ? (
          <div className="h-[125px] w-[125px] shrink-0 overflow-hidden rounded-[8px] bg-[#D9D9D9]">
            <PrismicNextImage
              field={member.photo}
              className="h-full w-full object-cover object-top"
              loading={isPriority ? 'eager' : 'lazy'}
              fetchPriority={isPriority ? 'high' : undefined}
              decoding={isPriority ? 'auto' : 'async'}
              imgixParams={{
                w: 250,
                h: 250,
                fit: 'crop',
                crop: ['faces', 'top'],
                auto: ['compress', 'format'],
                q: 75,
              }}
              sizes="125px"
              fallbackAlt=""
            />
          </div>
        ) : null;

        const socialRow =
          socialPairs.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {socialPairs.map((pair, linkIndex) => (
                <SocialLinkItem
                  key={linkIndex}
                  pair={pair}
                  memberName={member.name}
                />
              ))}
            </div>
          ) : null;

        if (isMobileViewPort) {
          return (
            <article
              key={index}
              className="group relative flex flex-col rounded-[10px] bg-[#f3f3f3] p-5 transition-all duration-300 ease-out hover:-translate-y-[2px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.16)] hover:ring-1 hover:ring-[#00f557]/40"
            >
              <div className="flex items-stretch gap-[16px]">
                {photoBlock}
                <div className="flex min-w-0 flex-1 flex-col">
                  {member.name && (
                    <h3 className="mt-[4px] text-[24px] font-semibold leading-[1.4] text-[#041118] [word-spacing:100vw]">
                      {member.name}
                    </h3>
                  )}
                  {socialRow && (
                    <div className="mb-[8px] mt-auto">{socialRow}</div>
                  )}
                </div>
              </div>
              {member.role && (
                <p className="mt-[16px] text-[18px] font-light leading-[1.5] text-[#041118]">
                  {member.role}
                </p>
              )}
              {member.subtitle && (
                <p className="mt-[2px] text-[13px] leading-[1.4] text-[#041118]/60">
                  {member.subtitle}
                </p>
              )}
            </article>
          );
        }

        return (
          <article
            key={index}
            className="group relative flex min-h-[165px] items-stretch gap-[24px] rounded-[10px] bg-[#f3f3f3] p-5 transition-all duration-300 ease-out hover:-translate-y-[2px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.16)] hover:ring-1 hover:ring-[#00f557]/40"
          >
            {photoBlock}

            <div className="flex min-w-0 flex-1 flex-col text-left">
              <div className="mt-[4px] flex flex-col gap-[2px]">
                {member.name && (
                  <h3 className="text-[24px] font-semibold leading-[1.4] text-[#041118]">
                    {member.name}
                  </h3>
                )}
                {member.role && (
                  <p className="text-[18px] font-light leading-[1.5] text-[#041118]">
                    {member.role}
                  </p>
                )}
                {member.subtitle && (
                  <p className="text-[13px] leading-[1.4] text-[#041118]/60">
                    {member.subtitle}
                  </p>
                )}
              </div>

              {socialRow && <div className="mb-[8px] mt-auto">{socialRow}</div>}
            </div>
          </article>
        );
      })}
    </div>
  );
};
